import { AppContent } from "../AppContent";
import { MetaContentValues } from "../metaContentValues";

const MortgagePaymentsRecalculation: AppContent['mortgagePaymentsRecalculation'] = {
    metaContent: MetaContentValues.mortgagePaymentsRecalculation,
    heading1: "Your yearly mortgage payment recalculation explained",
    usefulGuide: "We've created this useful guide to help understand how your yearly mortgage payment recalculation works.",
    keepOnTrack: "Keeping your monthly payments on track",
    youCanWatch: {
        paragraph: "You can watch our explainer video to find out more about your yearly mortgage recalculation and what it means.",
    },
    ifYouPrefer: {
        paragraph1: "If you prefer to read about it or want to know more after you've watched the video, you can find out the following and more on this page.",
        bulletPoint1: "What is a yearly mortgage payment recalculation?",
        bulletPoint2: "Why could your monthly mortgage payments change?",
        bulletPoint3: "What happens if I've missed any payments?"
    },
    videoLink: {
        title: "Annual Review of Mortgage Monthly Payments - Birmingham Midshires",
        id: "PBJlvtT7-v0"
    },
    thingsToKnow: {
        header1: "Things you need to know",
        interestOnly: {
            interestHeader: "If your mortgage is interest only",
            paragraph1: "We'll make sure you're paying the right amount of interest based on your balance. We'll let you know what your interest only balance is in your annual statement.",
            paragraph2: "It's your responsibility to make sure you have enough money to repay the balance at the end of its term. If you don't think your plans will be on track to repay your mortgage when it gets to the end of the agreed term, please contact us. The earlier you get in touch, the more opportunity you have to make alternative arrangements.",
            paragraph3: "If your mortgage isn't fully paid off by the end of its term, we'll continue to charge interest on the balance until it is. Once your mortgage reaches the end of its term, we'll no longer recalculate your monthly payment. This could mean your balance will increase.",
        },
        continuePreviousPayments: {
            paymentHeader: "If my payment has gone down, can I continue to pay the amount I was paying before?",
            paragraph1: "Yes. The easiest way to do this is by making regular overpayments.",
            paragraph2: "Some mortgages have charges for repaying all or part of the mortgage back within a certain time. We call these early repayment charges (ERCs).",
            paragraph3: "Where ERCs apply, you can usually make overpayments of up to 10% of the amount owed as of 01 January. If you overpay more than 10% over the year, we'll only charge you on the balance you overpay above 10%.",
            furtherInformation: {
                text: "Further information on overpayments.",
                url: "https://www.bmmortgages.co.uk/knowledge-base/making-overpayments"
            },
            erc: "Early repayment charges (ERCs) "
        },
        missedPayment: {
            missedHeader: "Missed a payment?",
            paragraph1: "Your new monthly payment won't include any missed payments. If you've missed any payments, you'll need to set up an arrangement to cover your arrears. Please call us on 0808 145 0372 so we can discuss your options. Lines are open Monday to Friday, 8am to 8pm and Saturday, 8am to 5pm. We're closed on Sundays and bank holidays.",
            paragraph2: "Your monthly payment will go up if you've missed previous payments. This is because when payments are missed, your balance will be higher than expected. The monthly payment will increase to cover the additional interest from the higher balance.",
            futurePayments: {
                text: "Worried about future payments?",
                url: "https://www.bmmortgages.co.uk/knowledge-base/worried-about-future-payments"
            }
        },
        reduceMortgage: {
            reduceHeader: "Can I reduce my mortgage term?",
            paragraph1: "You'll need to speak to us if you want to do this. We'll explain how changing the term will impact the total amount you pay, your monthly payment, and how your payments might change in the future."
        }
    },
    frequentQuestions: {
        frequentlyAsked: "Frequently asked questions",
        updatePayments: {
            paymentQuery: "Why do you need to update my payment?",
            accordion: {
                paragraph1: "When you took out your mortgage, you agreed to repay it by the end of its term or cover your interest payments if you have an interest-only mortgage. You'll do this by paying us an agreed amount on an agreed date every month."
            }
        },
        smallPaymentChange: {
            smallQuery: "Why has my payment changed by a small amount?",
            accordion: {
                paragraph1: "Although your payment is the same every month, the amount of interest charged varies depending on the number of days in the month. So, you'll be charged 30 days' interest in September and 31 days' interest in October.",
                paragraph2: "It might also change by a small amount if your normal payment date falls on a Saturday, Sunday, or a bank holiday and we have to collect it a day or so later than normal."
            }
        },
        fixedInterestChange: {
            interestQuery: "I'm on a fixed interest rate, can my monthly payment change?",
            accordion: {
                paragraph1: "Yes. Although your interest rate won't change, there can be other reasons why the amount you pay might change."
            }
        },
        paymentChangeReplacement: {
            changeQuery: "I already have a payment arrangement in place, will this change?",
            accordion: {
                paragraph1: "If any changes impact your existing arrangements, we'll let you know you in your yearly statement."
            }
        },
        paymentChange: {
            multipleQuery: "Do I need to do anything if my payment changes?",
            accordion: {
                heading1: "If you pay by direct debit, you don't need to do anything to change your payment",
                paragraph1: "We'll take the new payment amount on your usual payment date.",
                heading2: "If you don't pay by direct debit and the amount you need to pay changes",
                paragraph2: "You'll need to pay the new amount in your usual way.",
                heading3: "If you have any payment arrangements in place and the amount you need to pay changes",
                paragraph3: "We'll let you know what this means for your arrangement in your annual statement."
            }
        },
        calculateInterest: {
            calculateQuery: "How do we calculate interest?",
            accordion: {
                paragraph1: "We calculate interest on a daily basis on most of our mortgages. This means if you make a payment, it'll reduce how much you owe and the amount we charge interest on from the day we actually receive the money.",
                paragraph2: "We don't always automatically recalculate your monthly payment when an overpayment's made, but we can update the payment if you ask us to."
            }
        }
    },
    backButton: "Back to Knowledge base",
};

export default MortgagePaymentsRecalculation;
